/**
 *  THE REDUX STORE
 *
 *  The store also has the thunk and logger middleware applied. The thunk
 *  middleware allows action generators to return functions that have a dispatch
 *  parameter sent by Redux.
 *
 *  @version 0.1
 */

import thunkMiddleware from 'redux-thunk';
import { createStore, applyMiddleware } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';

import { GET } from './constants.js';

import rootReducer from './pages/Main/Main.reducers.js';

import {
    PAGE_NAME_REVIEW_AND_ACTIVATE,
    PAGE_NAME_MANAGE_ENTITIES,
    PAGE_NAME_MANAGE_CATEGORIES,
    PAGE_NAME_MANAGE_FEEDS,
    PAGE_NAME_MANAGE_ALERTS,
    INACTIVE_ENTITIES_DEFAULT_SORT,
    ENTITIES_DEFAULT_SORT,
    CATEGORIES_DEFAULT_SORT,
    MANAGE_FEEDS_DEFAULT_SORT,
    ALERTS_DEFAULT_SORT
} from './constants';

export const initialState = {

    user: {
        pguidTranslations: null,
        isLoggedIn:        false,
        helpUrlRoot:       '',
        lnasAuth: false,
        permissions: [],
        appSettings:    {
            authenticationInformation: {
                lnasLoginUrl: null,
                lnasLogoutUrl: null
            },
            isDossierEnabled: false,
            isExperianEnabled: false,
            isAlertsEnabled: true,
            isPendoEnabled: false,
            lineChartEnabled: true,
            accountPageEnabled: false,
            isAuditUsersDownloadEnabled: false,
            isAuditEntitiesDownloadEnabled: false,
            minutesToCategoryDeletion: 43200,
            cisIDs:[]
        },
        preferences:       {
            language:               'en-us',
            metadataLanguage:       '',
            articlesLanguageFilter: 'all',
            previewCompany: {
                language: "all",
                riskAngle: "all",
                riskLevel: "all",
                sortBy: "date",
                direction: "DESC",
                refreshToggle: 0
            },
            pagesTablesSort: {
                [PAGE_NAME_REVIEW_AND_ACTIVATE]: INACTIVE_ENTITIES_DEFAULT_SORT,
                [PAGE_NAME_MANAGE_ENTITIES]:     ENTITIES_DEFAULT_SORT,
                [PAGE_NAME_MANAGE_CATEGORIES]:   CATEGORIES_DEFAULT_SORT,
                [PAGE_NAME_MANAGE_FEEDS]:        MANAGE_FEEDS_DEFAULT_SORT,
                [PAGE_NAME_MANAGE_ALERTS]:       ALERTS_DEFAULT_SORT
            },
            dashboard:              {
                isPodsSectionCollapsed:                           false,
                isDashboardFiltersCollapsed:                      false,
                isDashboardFiltersNarrowByCollapsed:              false,
                isDashboardFiltersEntitiesCollapsed:              false,
                isDashboardFiltersRiskAnglesCollapsed:            false,
                isDashboardFiltersRiskLevelsCollapsed:            false,
                isDashboardFiltersExperianCreditRatingCollapsed:  false,
                isDashboardFiltersSanctionsAndWatchlistCollapsed: false,
                isDashboardFiltersDateRangeCollapsed:             false,
                isDashboardFiltersLanguageCollapsed:              false,
                newsVolumePodGraphStyle:                          'bar'
            },
            filters:                {
                entitiesFilter: {
                    type: "all_entities",
                    entitiesId: [],
                    categories: {
                        allCategories: true,
                        allUnassigned: true,
                        categoriesId: []
                    }
                },
                pestleFilter: {
                    political: true,
                    economic: true,
                    social:true,
                    technological:true,
                    legal:true,
                    environmental:true
                },
                riskFilter: {
                    high: true,
                    medium: false,
                    low: false
                },
                daysFilter: 30,
                languageFilter: {
                    en: true,
                    de: true,
                    fr: true
                }
            },
            userAppVersion: ''
        },
        pendingUpload:  {
            count:    0,
            uploadId: null
        },
        productAuthorizations: []
    },

    header: {
        menuStateClass: '',
        subMenuMessage: '',
        helpUrl:        '',
        version:        "-"
    },

    batchUpload: {
        stage:                       'initial',
        file:                        null,
        uploadId:                    null,
        checkFormattingEntriesCount: 'N/A'
    },

    reviewCompanies: {
        stage:                  'initial',
        warningDialog:          '',
        categoryStore:          null,
        previewItems:           [],
        isPreviewAll:           false,
        selectedItems:          [],
        totalCount:             null,
        filterCount:            null,
        saveButtonDisabled:     true,
        activateButtonDisabled: true,
        urlParams:              {
            sortBy:           'companyTitleSort',
            direction:        'asc',
            categoryIdFilter: null,
            refreshToggle:    0
        }
    },

    feedListing: {
        stage:         'rss-listing-warning-hidden',
        categoryStore: null,
        selectedItems: [],
        totalCount:    null,
        urlParams:     {
            sortBy:        'lastUpdated',
            direction:     'desc',
            refreshToggle: 0
        }
    },


    previewCompany: {
        stage:                'initial',
        companyData:          null,
        nextCompany:          null,
        previousCompany:      null,
        currentCompany:       null,
        columnWidths:         [46, 11, 17, 26],
        urlParams: {
            language:      'all',
            riskAngle:     'all',
            riskLevel:     'all',
            sortBy:        'date',
            direction:     'DESC',
            refreshToggle: 0
        },
        articleCount:         null,
        filterCount:          null,
        previewEntitiesCount: 1
    },

    spinners: {

        /* The main spinner is the only one whose value is a number
         * (a counter of how many main spinner calls have been made).
         */
        mainSpinner:     {count: 0},
        fullPageSpinner: false

    },

    dashboardState: {
        dashboardStage: null,
        dashboardInfo:  {
            'inactive':     "N/A",
            'active':       "N/A",
            'rss':          "N/A",
            'subscription': "N/A",
            'available':    "N/A",
            spinners:       {
                'addSpinner':      true,
                'activateSpinner': true,
                'shareSpinner':    true,
                'manageSpinner':   true
            }
        },
    },

    addRssFeed: {
        isFeedNameValid: false,
        feedInformation: {
            feedName: ''
        },
        thirdParties:    {
            categoryNames:          {},
            selectedCategories:     [],
            selectedThirdParties:   [],
            allCategoriesSelected:  false,
            thirdPartySelectionBox: 'all-third-parties-section',
            loadingSpinner:         0,
            totalCount:             null,
            filterCount:            null,
            urlParams:              {
                sortBy:           'companyTitleSort',
                direction:        'asc',
                categoryIdFilter: null,
                refreshToggle:    0
            }
        },
        riskAngles:      {
            political:     3,
            economic:      3,
            social:        3,
            technological: 3,
            legal:         3,
            environmental: 3
        },
        saveErrors:      []
    },

    editThirdParty: {
        companyData: null
    },

    thirdPartyPages: {
        companyData: null,
        isReturningFromArticlePage: false
    },

    addNewCategoryModal: {
        validationMessage: ''
    },

    viewArticlePage: {
        initialData:        [],
        url:                null,
        totalCount:         0,
        urlParams:          null,
        requestType:        GET,
        requestPayload:     null,
        embeddedResultList: null
    },

    popupMessages: {
        popupTitle: null,
        popupCount: 0,
        messages:   []
    },

    sanctionsWarnings: {
        singleEntity: {
            cisIDs: [],
            isLoading: false,
            totalCount: null,
            entityName: null, // entity name for which query is made
            companyId: null,
            items : [],
            hasError: false,
            isFinished: false
        }
    },

    backgroundMessages: {
        title: null,
        message: null,
        messageParameters: '',
        icon: null,
        type: '',
        isVisible: false,
        shrink: false,
        close: false,
        link: null
    }
};

export const reduxStore = createStore(
    rootReducer,
    initialState,
    composeWithDevTools(
        applyMiddleware(
            thunkMiddleware
        )
    )
);

export default reduxStore;




// WEBPACK FOOTER //
// ./src/scripts/reduxStore.js